"use client"

import { Image } from "@/app/components/image"
import { ModelViewer } from "@/app/components/model-viewer"
import { MuteIcon } from "@/app/icons/mute"
import { UnmuteIcon } from "@/app/icons/unmute"
import { DropPreviewType } from "@anaconda/core/drop/types"
import { Button } from "@rarible_int/anaconda_ui_components"
import { type VideoHTMLAttributes, useEffect, useRef, useState } from "react"

interface Props {
  src?: string
  alt?: string
  type?: DropPreviewType
  className?: string
  posterUrl?: string
  videoProps?: VideoHTMLAttributes<HTMLVideoElement>
}

export const PLACEHOLDER_IMAGE_SRC = "/images/placeholder.svg"

export function ItemMedia({
  src,
  alt,
  type = DropPreviewType.Image,
  className,
  posterUrl,
  videoProps,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isMuted, setIsMuted] = useState(videoProps?.muted ?? true)
  const [isPlaying, setIsPlaying] = useState(false)
  const [videoError, setVideoError] = useState(false)

  // Reset video error state when src or type changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setVideoError(false)
    setIsPlaying(false)
  }, [src, type])

  if (type === DropPreviewType.ThreeD && src) {
    return (
      <ModelViewer
        src={src}
        alt={alt ?? "3D NFT Model"}
        className={className}
        style={{
          width: "100%",
          height: "100%",
        }}
      />
    )
  }

  if (type === DropPreviewType.Video && src) {
    const handleVideoError = (
      e: React.SyntheticEvent<HTMLVideoElement, Event>,
    ) => {
      // If video fails to load, fall back to showing the poster or placeholder
      setVideoError(true)
    }

    const handlePlay = () => {
      setIsPlaying(true)
    }

    const handlePause = () => {
      setIsPlaying(false)
    }

    // If video failed to load, show poster or placeholder as fallback
    if (videoError) {
      return (
        <Image
          imageSrc={posterUrl ?? PLACEHOLDER_IMAGE_SRC}
          alt={alt ?? "Video thumbnail"}
          className={className}
        />
      )
    }

    return (
      <div className="relative h-full w-full">
        <video
          ref={videoRef}
          className={`${className} h-full w-full object-contain`}
          src={src}
          poster={posterUrl}
          autoPlay={videoProps?.autoPlay ?? true}
          muted={isMuted}
          loop={videoProps?.loop ?? true}
          playsInline={videoProps?.playsInline ?? true}
          preload="metadata"
          controls={videoProps?.controls ?? true}
          onError={handleVideoError}
          onPlay={handlePlay}
          onPause={handlePause}
        >
          <track kind="captions" />
          Your browser does not support the video tag.
        </video>
      </div>
    )
  }

  return (
    <Image
      imageSrc={src ?? PLACEHOLDER_IMAGE_SRC}
      alt={alt ?? ""}
      className={className}
    />
  )
}
